import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/firebase";
import md5 from "md5";
import { mapCsvRowToBaseIngredient } from "@/service/base_ingredient_service";
/**
 * Importe en DB dans HiddenBaseIngredients/all
 * en vérifiant les doublons (par nom)
 */
export async function importRowsToHiddenBaseIngredients(rows) {
  const docRef = doc(db, "HiddenBaseIngredients", "all");
  const snap = await getDoc(docRef);
  const existing = snap.exists() ? snap.data() : {};

  let toInsert = {};

  for (const row of rows) {
    const ingredientObj = mapCsvRowToBaseIngredient(row);
    const [id, ing] = Object.entries(ingredientObj)[0];

    // Doublon ? Vérif par nom
    const already = Object.values(existing).some(
      (e) => e.productName.toLowerCase().trim() === ing.productName.toLowerCase().trim()
    );

    if (!already) {
      toInsert[id] = { ...ing, hidden: true };
    }
  }

  if (Object.keys(toInsert).length > 0) {
    await setDoc(docRef, toInsert, { merge: true });
  }

  return { added: Object.keys(toInsert).length };
}

/**
 * Transforme une ligne CSV en format Firestore pour BaseIngredients
 */
export function mapCsvRowToHiddenBaseIngredient(row) {
  const id = generateId(row["ingrédients"], row["marque"], row["type"]);

  return {
    [id]: {
      productName: row["ingrédients"] || "",
      productBrand: row["marque"] || "",
      category: row["type"] || "",
      servingQuantity: row["poids (g)"] || null,
      servingSize: row["poids (g)"] ? row["poids (g)"] + " g" : null,
      added_dt: new Date().toISOString(),
      novaGroup: { color: "green", string: "nova.1" },
      isEditingDisabled: true,
      isBaseIngredient: true,
      hidden: true,
      nutriments_map: [
        { key: "energy-kcal", value: row["kcal./100g"] ? row["kcal./100g"] + " kcal" : "" },
        { key: "fat", value: row["lipides"] ? row["lipides"] + " g" : "" },
        { key: "carbohydrates", value: row["glucides"] ? row["glucides"] + " g" : "" },
        { key: "proteins", value: row["protéines"] ? row["protéines"] + " g" : "" },
        { key: "fiber", value: row["fibres"] ? row["fibres"] + " g" : "" },
        { key: "salt", value: row["Sel"] ? row["Sel"] + " g" : "" },
        { key: "sugar", value: row["Sucre"] ? row["Sucre"] + " g" : "" },
        { key: "saturated-fat", value: row["AG saturés"] ? row["AG saturés"] + " g" : "" },
      ].filter((n) => n.value),
    },
  };
}

/**
 * Génère un ID déterministe basé sur nom + marque + type
 */
function generateId(productName, productBrand = "", category = "") {
  // include name + brand in a readable prefix and append an md5 hash
  const name = String(productName || "").trim();
  const brand = String(productBrand || "").trim();
  const raw = `${name}|${brand}|${category || ''}`;
  const hash = md5(raw);

  const sanitize = (s) =>
    String(s || "")
      // normalize accents (NFD) then remove diacritics so é -> e, ô -> o, etc.
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  // Use only the sanitized name as readable prefix, and append a short md5 suffix
  const prefix = sanitize(name) || hash.slice(0, 8);
  const shortHash = hash.slice(0, 8); // 8 chars is concise and unlikely to collide in this dataset
  return `${prefix}_${shortHash}`;
}

/**
 * Récupère les ingrédients de base (collection partagée)
 */
export async function getHiddenBaseIngredients() {
  const docRef = doc(db, "HiddenBaseIngredients", "all");
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return {};

  return docSnap.data();
}

/**
 * Supprime un ingrédient de base (hidden) du document `all`.
 * Vérifie que le rôle utilisateur est admin.
 */
export async function deleteHiddenBaseIngredient(ingredientId, userRole) {
  if (!ingredientId) return;
  if (userRole !== "admin") {
    throw new Error("Unauthorized: admin role required");
  }

  const docRef = doc(db, "HiddenBaseIngredients", "all");
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const data = snap.data();
    if (!Object.prototype.hasOwnProperty.call(data, ingredientId)) return;
    delete data[ingredientId];
    await setDoc(docRef, data);
  } catch (err) {
    console.error("Erreur deleteHiddenBaseIngredient :", err);
    throw err;
  }
}
